import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import queryClient from '../lib/queryClient';
import useLocalStorage from '../hooks/useLocalStorage';

// Import hooks for data fetching
import { useUsers } from '../hooks/useUsers';
import { useRoles } from '../hooks/useRoles';
import { useSubscription } from '../hooks/useSubscriptions';
import { useAds } from '../hooks/useAds';
import { useDashboard } from '../hooks/useDashboard';

// Create context
const DataContext = createContext();

// Provider component
export function DataProvider({ children }) {
  // Track active filters and sort options
  const [filters, setFilters] = useLocalStorage('app_filters', {
    users: { status: 'all', subscription: 'all', search: '' },
    roles: { search: '' },
    subscriptions: { status: 'all', search: '' },
    ads: { status: 'all', search: '' }
  });
  
  // Track sort options
  const [sort, setSort] = useLocalStorage('app_sort', {
    users: { field: 'joinDate', direction: 'desc' },
    roles: { field: 'name', direction: 'asc' },
    subscriptions: { field: 'price', direction: 'asc' },
    ads: { field: 'name', direction: 'asc' }
  });
  
  // Track pagination state
  const [pagination, setPagination] = useState({
    users: { page: 1, limit: 10 },
    roles: { page: 1, limit: 10 },
    subscriptions: { page: 1, limit: 10 },
    ads: { page: 1, limit: 10 }
  });
  
  // Fetch data using hooks
  const usersData = useUsers({
    filters: filters.users,
    sort: sort.users,
    pagination: pagination.users
  });
  
  const rolesData = useRoles({
    search: filters.roles.search
  });
  
  const subscriptionsData = useSubscription({
    filters: filters.subscriptions,
    sort: sort.subscriptions
  });
  
  const adsData = useAds();
  
  const dashboardData = useDashboard();
  
  // Callbacks for updating filters, sort, and pagination
  const updateFilters = useCallback((section, newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [section]: {
        ...prevFilters[section],
        ...newFilters
      }
    }));
    
    // Reset pagination when filters change
    setPagination(prevPagination => ({
      ...prevPagination,
      [section]: {
        ...prevPagination[section],
        page: 1
      }
    }));
  }, [setFilters]);
  
  const updateSort = useCallback((section, field, direction) => {
    setSort(prevSort => ({
      ...prevSort,
      [section]: { field, direction }
    }));
  }, [setSort]);
  
  const updatePagination = useCallback((section, newPagination) => {
    setPagination(prevPagination => ({
      ...prevPagination,
      [section]: {
        ...prevPagination[section],
        ...newPagination
      }
    }));
  }, []);
  
  // Clear all filters for a section
  const clearFilters = useCallback((section) => {
    const defaultFilters = {
      users: { status: 'all', subscription: 'all', search: '' },
      roles: { search: '' },
      subscriptions: { status: 'all', search: '' },
      ads: { status: 'all', search: '' }
    };
    
    setFilters(prevFilters => ({
      ...prevFilters,
      [section]: defaultFilters[section]
    }));
    
    // Reset pagination
    setPagination(prevPagination => ({
      ...prevPagination,
      [section]: {
        ...prevPagination[section],
        page: 1
      }
    }));
  }, [setFilters]);
  
  // Reset all data state
  const resetDataState = useCallback(() => {
    const defaultFilters = {
      users: { status: 'all', subscription: 'all', search: '' },
      roles: { search: '' },
      subscriptions: { status: 'all', search: '' },
      ads: { status: 'all', search: '' }
    };
    
    const defaultSort = {
      users: { field: 'joinDate', direction: 'desc' },
      roles: { field: 'name', direction: 'asc' },
      subscriptions: { field: 'price', direction: 'asc' },
      ads: { field: 'name', direction: 'asc' }
    };
    
    const defaultPagination = {
      users: { page: 1, limit: 10 },
      roles: { page: 1, limit: 10 },
      subscriptions: { page: 1, limit: 10 },
      ads: { page: 1, limit: 10 }
    };
    
    setFilters(defaultFilters);
    setSort(defaultSort);
    setPagination(defaultPagination);
    
    // Invalidate all queries to reload data
    queryClient.invalidateQueries();
  }, [setFilters, setSort]);
  
  // Export all data and methods
  const value = {
    // Data
    users: usersData.users,
    roles: rolesData.roles,
    subscriptions: subscriptionsData.subscriptions,
    ads: adsData,
    dashboard: dashboardData,
    
    // Loading states
    isLoadingUsers: usersData.isLoading,
    isLoadingRoles: rolesData.isLoading,
    isLoadingSubscriptions: subscriptionsData.isLoading,
    
    // Error states
    usersError: usersData.error,
    rolesError: rolesData.error,
    subscriptionsError: subscriptionsData.error,
    
    // Meta information (pagination, etc.)
    usersMeta: usersData.meta,
    
    // Filters, sort, and pagination state
    filters,
    sort,
    pagination,
    
    // Methods for updating state
    updateFilters,
    updateSort,
    updatePagination,
    clearFilters,
    resetDataState,
    
    // CRUD operations
    createUser: usersData.createUser,
    updateUser: usersData.updateUser,
    deleteUser: usersData.deleteUser,
    
    createRole: rolesData.createRole,
    updateRole: rolesData.updateRole,
    deleteRole: rolesData.deleteRole,
    
    createSubscription: subscriptionsData.createSubscription,
    updateSubscription: subscriptionsData.updateSubscription,
    deleteSubscription: subscriptionsData.deleteSubscription,
    
    updateAdsSettings: adsData.updateAdsSettings,
    createAdCampaign: adsData.createAdCampaign,
    updateAdCampaign: adsData.updateAdCampaign,
    deleteAdCampaign: adsData.deleteAdCampaign,
    
    // Loading states for mutations
    isCreatingUser: usersData.isCreating,
    isUpdatingUser: usersData.isUpdating,
    isDeletingUser: usersData.isDeleting,
    
    isCreatingRole: rolesData.isCreating,
    isUpdatingRole: rolesData.isUpdating,
    isDeletingRole: rolesData.isDeleting,
    
    isCreatingSubscription: subscriptionsData.isCreating,
    isUpdatingSubscription: subscriptionsData.isUpdating,
    isDeletingSubscription: subscriptionsData.isDeleting,
    
    isUpdatingAdsSettings: adsData.isUpdatingSettings,
    isCreatingAdCampaign: adsData.isCreatingCampaign,
    isUpdatingAdCampaign: adsData.isUpdatingCampaign,
    isDeletingAdCampaign: adsData.isDeletingCampaign
  };
  
  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

// Custom hook to use the data context
export function useData() {
  const context = useContext(DataContext);
  
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  
  return context;
}